import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const KEY = "cookie_consent";
const readConsent = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-[#2A2A2A] last:border-0">
    <div>
      <div className="text-white text-[15px] font-medium">{label}</div>
      {hint && <p className="text-[13px] text-neutral-500 mt-0.5">{hint}</p>}
    </div>
    <button type="button" role="switch" aria-checked={checked} data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${checked ? "bg-[#1A8A4A]" : "bg-[#3A3A3A]"}`}>
      <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  </div>
);

const CookieConsent = () => {
  const { t } = useLang();
  const location = useLocation();
  const [decided, setDecided] = useState(true); // hidden until we know
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  // Internal console: never overlay the admin UI with the consent bar (settings modal still works)
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/Admin");

  useEffect(() => {
    const c = readConsent();
    setDecided(!!c);
    if (c) { setAnalytics(!!c.analytics); setMarketing(!!c.marketing); }
    const openSettings = () => {
      const cur = readConsent();
      if (cur) { setAnalytics(!!cur.analytics); setMarketing(!!cur.marketing); }
      setShowSettings(true);
    };
    window.addEventListener("open_cookie_settings", openSettings);
    return () => window.removeEventListener("open_cookie_settings", openSettings);
  }, []);

  const save = (consent) => {
    localStorage.setItem(KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
    setDecided(true);
    setShowSettings(false);
  };

  const acceptAll = () => save({ accepted: true, analytics: true, marketing: true });
  const rejectNonEssential = () => save({ accepted: false, analytics: false, marketing: false });
  const savePreferences = () => save({ accepted: analytics || marketing, analytics, marketing });

  return (
    <>
      {!decided && !isAdminRoute && (
        <div className="fixed bottom-0 inset-x-0 z-[80] bg-[#141414] border-t border-[#2A2A2A]" data-testid="cookie-bar">
          <div className="max-w-[1200px] mx-auto px-4 py-2.5 flex items-center gap-3">
            <p className="text-[12px] leading-snug text-neutral-400 flex-1 min-w-0">{t("cookie.notice")}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setShowSettings(true)} data-testid="cookie-settings-link"
                className="hidden sm:inline text-[12px] text-neutral-400 hover:text-white underline px-2 h-8">{t("cookie.settings")}</button>
              <button onClick={rejectNonEssential} data-testid="cookie-reject"
                className="h-8 px-3 rounded-md border border-[#3A3A3A] text-white text-[12px] font-medium hover:border-[#555] transition-colors whitespace-nowrap">{t("cookie.reject")}</button>
              <button onClick={acceptAll} data-testid="cookie-accept" style={{ backgroundColor: "#1A8A4A" }}
                className="h-8 px-3.5 rounded-md text-white text-[12px] font-semibold transition-colors whitespace-nowrap">{t("cookie.accept_all")}</button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center px-4" data-testid="cookie-settings-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
          <div className="relative w-full max-w-[440px] bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-white font-semibold text-[18px]">{t("cookie.settings_title")}</h3>
              <button onClick={() => setShowSettings(false)} data-testid="cookie-settings-close" className="text-neutral-500 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-[13px] text-neutral-500 mb-3">{t("cookie.manage")}</p>
            <div>
              <div className="flex items-center justify-between gap-4 py-3 border-b border-[#2A2A2A]">
                <div>
                  <div className="text-white text-[15px] font-medium">{t("cookie.necessary")}</div>
                  <p className="text-[13px] text-neutral-500 mt-0.5">{t("cookie.necessary_hint")}</p>
                </div>
                <span className="text-[13px] text-neutral-500">{t("cookie.always_on")}</span>
              </div>
              <Toggle label={t("cookie.analytics")} hint={t("cookie.analytics_hint")} checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
              <Toggle label={t("cookie.marketing")} hint={t("cookie.marketing_hint")} checked={marketing} onChange={setMarketing} testid="cookie-toggle-marketing" />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={rejectNonEssential} data-testid="cookie-settings-reject"
                className="flex-1 h-11 rounded-lg border border-[#3A3A3A] text-white text-[14px] font-medium hover:border-[#555] transition-colors">{t("cookie.reject_all")}</button>
              <button onClick={savePreferences} data-testid="cookie-settings-save" style={{ backgroundColor: "#FF5C00" }}
                className="flex-1 h-11 rounded-lg text-white text-[14px] font-semibold transition-colors">{t("cookie.save")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
